/* ==========================================================================
   KAVKA SPIN  (assets/kavka-spin.js)
   The 360° viewer: <kavka-spin>. Used by the product gallery
   (snippets/kavka-gallery.liquid) and the front-page spin section.

   HOW TO USE IT ANYWHERE
     <script src="{{ 'kavka-spin.js' | asset_url }}" defer></script>
     <kavka-spin data-hint="Drag to rotate" style="display:block;aspect-ratio:1/1">
       <script type="application/json" data-spin-frames>[ …frame addresses… ]</script>
     </kavka-spin>
   The frame list can hold plain image addresses, or {"m": metal, "u": address}
   entries (the product gallery uses those to pick a metal's own spin).
   Frames are sorted by their number (_0001, _0002 …) and only download when
   the spin is opened (inside the gallery) or scrolled into view (anywhere
   else). It turns once by itself when loaded, with arrows showing the
   direction; drag sideways to turn it.
   ========================================================================== */
(function () {
  if (window.customElements && customElements.get('kavka-spin')) return;

  // ---- its look (added to the page once) ----
  var css = `
kavka-spin { display: block; position: relative; width: 100%; height: 100%; }
.kg-spin-canvas {
  display: block; width: 100%; height: 100%;
  touch-action: pan-y;           /* sideways drag turns the ring; up/down still scrolls the page */
  cursor: grab;
  user-select: none; -webkit-user-select: none;
}
.kg-spin-canvas:active { cursor: grabbing; }
.kg-spin-load {
  position: absolute; left: 50%; top: 50%;
  width: 56px; height: 56px; margin: -28px 0 0 -28px;
  border-radius: 50%;
  border: 3px solid rgba(217, 178, 142, 0.25);
  border-top-color: var(--kg-accent, #d9b28e);
  animation: kg-turn 0.8s linear infinite;
  pointer-events: none;
  transition: opacity 0.3s ease;
}
kavka-spin.is-ready .kg-spin-load { opacity: 0; animation: none; }
.kg-spin-hint {
  position: absolute; left: 50%; bottom: 10%;
  transform: translateX(-50%);
  padding: 6px 12px;
  font-size: 12px; line-height: 1; white-space: nowrap; color: #6f6863;
  background: rgba(255, 249, 239, 0.85);
  border: 1px solid rgba(217, 178, 142, 0.6); border-radius: 999px;
  pointer-events: none;
  opacity: 0; transition: opacity 0.4s ease;
}
kavka-spin.is-ready:not(.was-dragged) .kg-spin-hint { opacity: 1; }
/* arrows showing which way it turns: only during the first, automatic turn */
.kg-spin-dir {
  position: absolute; left: 12%; right: 12%; top: 62%;
  pointer-events: none;
  opacity: 0; transition: opacity 0.35s ease;
}
.kg-spin-dir svg { display: block; width: 100%; height: auto; overflow: visible;
  fill: none; stroke: var(--kg-accent, #d9b28e); stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round;
  filter: drop-shadow(0 0 2px rgba(255, 249, 239, 0.95)); }
kavka-spin.is-demo .kg-spin-dir { opacity: 1; }
@keyframes kg-turn { to { transform: rotate(360deg); } }
`;
  var style = document.createElement('style');
  style.id = 'kavka-spin-css';
  style.textContent = css;
  document.head.appendChild(style);

  // ======================================================================
  // <kavka-spin>: the 360° viewer. Frames come from the JSON inside it:
  //   [{"m":"yellow","u":"//…/Spin_…_0001.webp?v=…"}, …]
  // Drag sideways to turn (touch: up/down still scrolls the page); it
  // turns once by itself when it first finishes loading; it keeps a
  // little momentum after letting go.
  // ======================================================================
  if (!customElements.get('kavka-spin')) {
    var SPIN_TURN_MS = 2600;          // the automatic first turn
    var DRAG_TURNS_PER_WIDTH = 1;     // one full turn per drag across the picture

    class KavkaSpin extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        var json = this.querySelector('[data-spin-frames]');
        var list = [];
        try { list = JSON.parse(json ? json.textContent : '[]'); } catch (e) {}
        // sort by frame number, grouped by metal ('all' = no metal in the name)
        var num = function (u) { var m = /_(\d{3,4})(?=[._])/.exec(u.split('/').pop()); return m ? parseInt(m[1], 10) : 0; };
        this.sets = {};
        // entries are {"m": metal, "u": address} — or just addresses
        list.forEach(function (f) {
          if (typeof f === 'string') f = { m: 'all', u: f };
          (this.sets[f.m] = this.sets[f.m] || []).push(f.u);
        }, this);
        for (var k in this.sets) this.sets[k].sort(function (a, b) { return num(a) - num(b); });
        this.metal = 'all';
        this.frame = 0;
        this.images = [];

        this.canvas = document.createElement('canvas');
        this.canvas.className = 'kg-spin-canvas';
        this.loader = document.createElement('div'); this.loader.className = 'kg-spin-load';
        this.hint = document.createElement('div'); this.hint.className = 'kg-spin-hint';
        this.hint.textContent = this.getAttribute('data-hint') || '';
        this.arrowsEl = document.createElement('div'); this.arrowsEl.className = 'kg-spin-dir';
        this.arrowsEl.setAttribute('aria-hidden', 'true');
        // a shallow curve under the ring with an arrowhead at each end
        this.arrowsEl.innerHTML = '<svg viewBox="0 0 200 44"><path d="M14 12 Q100 52 186 12"/>' +
          '<path d="M14 12 l2 11 M14 12 l11 -1"/><path d="M186 12 l-2 11 M186 12 l-11 -1"/></svg>';
        this.append(this.canvas, this.loader, this.arrowsEl, this.hint);
        this.ctx = this.canvas.getContext('2d');

        var self = this;
        if (window.ResizeObserver) new ResizeObserver(function () { self.resize(); }).observe(this);
        this.bindDrag();

        // on its own (e.g. the front page): start when it scrolls into view
        if (!this.closest('kavka-gallery') && window.IntersectionObserver) {
          var io = new IntersectionObserver(function (es) {
            if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); self.activate(); }
          }, { rootMargin: '200px' });
          io.observe(this);
        }
      }

      currentSet() { return this.sets[this.metal] || this.sets.all || this.sets[Object.keys(this.sets)[0]] || []; }

      // the gallery tells the spin which metal is chosen
      setMetal(m) {
        var before = this.currentSet();
        this.metal = m || 'all';
        if (this.currentSet() !== before && this.loaded) { this.loaded = false; this.activate(); }
      }

      // start downloading the frames (sized to the picture) and show them
      activate() {
        this.active = true;
        if (this.loaded) {
          this.draw();
          // everything arrived while it was out of view: do the first turn now
          if (this._complete && !this._turned) this.allLoaded();
          return;
        }
        var urls = this.currentSet();
        if (!urls.length) return;
        this.loaded = true;
        this._complete = false;
        this.classList.remove('is-ready');
        var self = this, n = urls.length, got = 0, token = {};
        this._token = token;
        var w = this.frameWidth();
        this.images = new Array(n);
        // frame 1 first, then every 5th (so a rough turn works early), then the rest
        var order = [0];
        for (var i = 5; i < n; i += 5) order.push(i);
        for (i = 1; i < n; i++) if (order.indexOf(i) === -1) order.push(i);
        order.forEach(function (idx) {
          var img = new Image();
          img.decoding = 'async';
          img.onload = img.onerror = function () {
            if (self._token !== token) return;
            img._ok = img.naturalWidth > 0;
            got++;
            if (idx === 0 || idx === self.frame) self.draw();
            if (got === 1) self.classList.add('is-ready');
            if (got === n) self.allLoaded();
          };
          var u = urls[idx];
          img.src = /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=' + w)
                                            : u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
          self.images[idx] = img;
        });
      }
      deactivate() { this.active = false; this.stopMotion(); this.classList.remove('is-demo'); }

      frameWidth() {
        var css = this.clientWidth || 600, dpr = Math.min(window.devicePixelRatio || 1, 2);
        return Math.min(1500, Math.max(400, Math.ceil(css * dpr / 100) * 100));
      }

      resize() {
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w = Math.round(this.clientWidth * dpr), h = Math.round(this.clientHeight * dpr);
        if (!w || !h) return;
        if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; this.draw(); }
      }

      // draw the wanted frame, or the nearest one that has arrived
      draw() {
        var n = this.images.length;
        if (!n || !this.canvas.width) return;
        var img = null;
        for (var d = 0; d < n && !img; d++) {
          var a = this.images[(this.frame + d) % n], b = this.images[(this.frame - d + n) % n];
          if (a && a._ok) img = a; else if (b && b._ok) img = b;
        }
        if (!img) return;
        var cw = this.canvas.width, ch = this.canvas.height;
        var s = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
        var w = img.naturalWidth * s, h = img.naturalHeight * s;
        this.ctx.clearRect(0, 0, cw, ch);
        this.ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
      }

      go(f) {
        var n = this.images.length || 1;
        var next = ((Math.round(f) % n) + n) % n;
        if (next !== this.frame) { this.frame = next; this.draw(); }
      }

      allLoaded() {
        this._complete = true;
        if (this._turned || !this.active) return;
        if (this.getAttribute('data-autospin') === 'off') return;   // drag only
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this._turned = true;
        var self = this, n = this.images.length, start = performance.now(), from = this.frame;
        this.stopMotion();
        this.classList.add('is-demo');              // direction arrows on
        (function step(now) {
          var t = Math.min(1, (now - start) / SPIN_TURN_MS);
          var e = 1 - Math.pow(1 - t, 2);          // gently slows down at the end
          self.go(from + e * n);
          if (t < 1 && !self._dragging && self.active) self._raf = requestAnimationFrame(step);
          else self.classList.remove('is-demo');   // finished, touched or left
        })(start);
      }

      stopMotion() { if (this._raf) cancelAnimationFrame(this._raf); this._raf = null; }

      bindDrag() {
        var self = this, c = this.canvas, startX = 0, startF = 0, lastX = 0, lastT = 0, v = 0;
        // any touch on the spin ends the demo (and its arrows)
        this.addEventListener('pointerdown', function () { self.classList.remove('is-demo'); }, true);
        c.addEventListener('pointerdown', function (e) {
          if (e.button && e.button !== 0) return;
          self.stopMotion();
          self._dragging = true;
          self.classList.add('was-dragged');
          startX = lastX = e.clientX; startF = self.frame; lastT = performance.now(); v = 0;
          try { c.setPointerCapture(e.pointerId); } catch (err) {}
        });
        c.addEventListener('pointermove', function (e) {
          if (!self._dragging) return;
          var n = self.images.length || 1;
          var perPx = n * DRAG_TURNS_PER_WIDTH / (c.clientWidth || 300);
          self.go(startF - (e.clientX - startX) * perPx);
          var now = performance.now(), dt = now - lastT;
          if (dt > 0) v = 0.8 * v + 0.2 * (-(e.clientX - lastX) * perPx / dt);   // frames per ms
          lastX = e.clientX; lastT = now;
        });
        function end() {
          if (!self._dragging) return;
          self._dragging = false;
          // a little momentum
          var f = self.frame, prev = performance.now();
          if (Math.abs(v) < 0.005) return;
          (function coast(now) {
            var dt = now - prev; prev = now;
            f += v * dt; v *= Math.pow(0.94, dt / 16);
            self.go(f);
            if (Math.abs(v) > 0.003) self._raf = requestAnimationFrame(coast);
          })(prev);
        }
        c.addEventListener('pointerup', end);
        c.addEventListener('pointercancel', end);   // e.g. the finger went up/down: page scroll
      }
    }
    customElements.define('kavka-spin', KavkaSpin);
  }


})();